// Copyright (C) 2023 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import { AccessDenied } from "../lib"
import { PurchaseOrderDTO } from "../models"
import { BaseClient } from "./BaseClient"
import ProcurementOrder from "./PurchaseOrder"
import { Location, TrackableDocument } from "./models"


class ProcurementClient extends BaseClient {

  orderFactory(): ProcurementOrder {
    return new ProcurementOrder(this)
  }

  async confirmInvoiceUpload(documentId: string, code: string, file: File): Promise<void> {
    await this.http.post(`/documents/${documentId}/confirm`, {
      "size": file.size
    },
    {
      headers: {
        "X-Access-Code": code
      }
    })
  }

  async place(dto: PurchaseOrderDTO) {
    await this.http.post('/orders', dto)
  }

  async getLocations(): Promise<Location[]> {
    const response = await this.http.get(`/locations`)
    return response.data.locations.map((location: any) => {
      return new Location(this, location)
    })
  }

  async getTrackableDocumentData(documentId: string, code: string): Promise<any> {
    const response = await this.http.get(`/documents/${documentId}`, {
      headers: {
        "X-Access-Code": code
      }
    })
    if (response.status === 401) {
      throw new AccessDenied()
    }
    return response.data
  }

  async getTrackableDocument(documentId: string, code: string): Promise<TrackableDocument> {
    return new TrackableDocument(this, code, await this.getTrackableDocumentData(documentId, code))
  }

  async getInvoiceUploadUrl(documentId: string, code: string): Promise<string> {
    const response = await this.http.get(`/documents/${documentId}/upload`, {
      headers: {
        "X-Access-Code": code
      }
    })
    return response.data.url
  }

  getBasePath(): string {
    return "/api/procurement"
  }

  async listOrders(): Promise<ProcurementOrder[]> {
    const response = await this.http.get('/orders')
    return response.data.items.map((dto: any) => {
      return new ProcurementOrder(this, dto)
    })
  }

  async updateDocument(documentId: string, code: string, data: any): Promise<void> {
    await this.http.patch(`/documents/${documentId}`, data, {
      headers: {
        "X-Access-Code": code
      }
    })
  }
}


export { ProcurementClient }